/**
 * DISPLAY-ONLY mirror of the server's cancellation-fee bands.
 *
 * The server is authoritative: `utils/refundCalculator.js#calculateTimingBasedFee`.
 * This file exists so every customer-facing page shows the SAME fee the server will
 * actually charge. It previously did not: the customer dashboard hardcoded a
 * 24h/100% - 48h/75% - 96h/50% ladder, which only agreed with the server because it
 * happened to match the DEFAULT tiers. The moment a policy is edited (or set to
 * `strict` / `free` / a named window), the dashboard promised one number and the
 * server charged another.
 *
 * Parity is pinned by `src/utils/__tests__/cancellationPolicyMirror.parity.test.js`
 * against `tests/fixtures/cancellationPolicyFixtures.json`, whose expectations were
 * GENERATED from the server function. `tests/cancellationPolicyFixture.spec.js` checks
 * the same fixture against the server, so neither end can drift alone.
 *
 * Reference implementation to keep in step (utils/refundCalculator.js):
 *   - no policy / disabled / "free"      -> 0
 *   - "strict"                           -> 100
 *   - feeTiers populated                 -> first tier (descending minHours) whose
 *                                           minHours <= hours; if none match, the
 *                                           LOWEST minHours tier's percent
 *   - "custom" with no tiers             -> the default tiers
 *   - otherwise                          -> hoursBeforeAppointment (or a named
 *                                           window) vs cancellationFeePercent
 */

export const DEFAULT_CUSTOM_FEE_TIERS = [
  { minHours: 0, feePercent: 100 },
  { minHours: 24, feePercent: 75 },
  { minHours: 48, feePercent: 50 },
  { minHours: 96, feePercent: 0 },
];

const NAMED_WINDOW_HOURS = { "24-hour": 24, "48-hour": 48, "72-hour": 72 };

/** The tiers the server would actually apply, or [] when it uses the single band. */
export function resolveEffectiveFeeTiers(policy) {
  if (!policy) return [];
  const declared = Array.isArray(policy.feeTiers)
    ? policy.feeTiers.filter((tier) => tier && typeof tier === "object")
    : [];
  if (declared.length > 0) return declared;
  return policy.policyType === "custom" ? DEFAULT_CUSTOM_FEE_TIERS : [];
}

/** The single-band window in hours (named policy types fall back to a number). */
export function resolveFeeWindowHours(policy) {
  const named = policy?.policyType ? NAMED_WINDOW_HOURS[policy.policyType] : null;
  return named || policy?.hoursBeforeAppointment || 24;
}

/**
 * @param {Number} hoursUntilAppointment
 * @param {Object} policy  settings.cancellationPolicy
 * @returns {Number} fee percent (0-100) the server will charge
 */
export function resolveCancellationFeePercent(
  hoursUntilAppointment,
  policy,
) {
  if (!policy || !policy.enabled || policy.policyType === "free") return 0;
  if (policy.policyType === "strict") return 100;

  const hours = Number(hoursUntilAppointment) || 0;
  const tiers = resolveEffectiveFeeTiers(policy);

  if (tiers.length > 0) {
    const sorted = [...tiers].sort((a, b) => b.minHours - a.minHours);
    for (const tier of sorted) {
      if (hours >= tier.minHours) return Number(tier.feePercent) || 0;
    }
    // Below every tier's minHours - the server applies the highest fee tier.
    return Number(sorted[sorted.length - 1].feePercent) || 0;
  }

  const feePercent = Number(policy.cancellationFeePercent) || 50;
  return hours < resolveFeeWindowHours(policy) ? feePercent : 0;
}

/**
 * Human labels for the policy bullets, derived from the live policy so the text can
 * never describe a different ladder than the numbers above.
 *
 * @returns {Array<{label: String}>}
 */
export function describeCancellationPolicy(policy) {
  if (!policy || !policy.enabled) {
    return [{ label: "No cancellation fee applies to this booking." }];
  }

  if (policy.policyType === "free") {
    return [{ label: "Free cancellation - no fee, whenever you cancel." }];
  }

  if (policy.policyType === "strict") {
    return [{ label: "Strict policy - cancelling returns nothing." }];
  }

  const tiers = resolveEffectiveFeeTiers(policy);

  if (tiers.length === 0) {
    const hours = resolveFeeWindowHours(policy);
    const feePercent = Number(policy.cancellationFeePercent) || 50;
    return [
      { label: `Less than ${hours} hours: ${feePercent}% charge` },
      { label: `${hours} hours or more: No charge` },
    ];
  }

  const ascending = [...tiers].sort((a, b) => a.minHours - b.minHours);

  return ascending.map((tier, index) => {
    const next = ascending[index + 1];
    const feePercent = Number(tier.feePercent) || 0;
    const charge = feePercent > 0 ? `${feePercent}% charge` : "No charge";

    if (!next) return { label: `More than ${tier.minHours} hours: ${charge}` };

    const from = tier.minHours > 0 ? ` (from ${tier.minHours}h)` : "";
    return { label: `Less than ${next.minHours} hours${from}: ${charge}` };
  });
}
